import * as React from 'react';

import DromoUploader from 'dromo-uploader-react';

import {Nut_Import_Dromo_Client} from 'nutshell-core/settings-constants';

import {Button} from 'shells/button/button';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Body} from 'shells/typography/body';

import type {User} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../../analytics-client';

import type {ColumnHook, DromoField, BulkRowHook} from './types';

type Props = {
    fields: Array<DromoField>;
    columnHooks?: Array<ColumnHook>;
    bulkRowHooks?: Array<BulkRowHook>;
    importIdentifier: string;
    importerTitle?: string;
    onImportComplete: (response?: any, metadata?: any) => void;
    styleOverrides: Record<any, any>;
    user: User | null | undefined;
    isLoading: boolean;
    error: string | null | undefined;
    variant?: 'primary' | 'secondary';
    templateDownloadOverrideUrl?: string;
};

export function DromoImportButton(props: Props) {
    const [isUploaderModalOpen, setIsUploaderModalOpen] = React.useState<boolean>(false);
    // @ts-expect-error
    const handleResults = (response, metadata) => {
        setIsUploaderModalOpen(false);
        props.onImportComplete(response, metadata);
    };

    const uploadStep = props.templateDownloadOverrideUrl
        ? {
              templateDownloadOverrideURL: props.templateDownloadOverrideUrl,
          }
        : undefined;

    return (
        <StackLegacy spacing={0}>
            <Button
                variant={props.variant || 'primary'}
                onClick={() => {
                    analyticsClient.track(
                        analyticsClient.EVENTS.TRACK_DROMO_IMPORT_IMPORTER_OPENED,
                        {type: props.importIdentifier}
                    );
                    setIsUploaderModalOpen(true);
                }}
                disabled={Boolean(props.isLoading || props.error)}
                size='big'
            >
                {props.isLoading ? 'Loading…' : 'Import'}
            </Button>
            {props.error && (
                <div className='mt-2'>
                    <Body size='small' color='error'>
                        Error loading data, please refresh the page or contact support.
                    </Body>
                </div>
            )}
            <DromoUploader
                open={isUploaderModalOpen}
                licenseKey={Nut_Import_Dromo_Client.SETTINGS_FRONTEND_API_KEY}
                // @ts-expect-error
                user={{id: props.user && props.user.id}}
                // @ts-expect-error - templateDownloadOverrideURL is supported at runtime but missing from Dromo's published types, see: https://developer.dromo.io/reference/settings/settings#param-template-download-override-url
                settings={{
                    importIdentifier: props.importIdentifier,
                    title: props.importerTitle ?? 'Import products',
                    backendSyncMode: 'FULL_DATA',
                    developmentMode: NutClientConfig.isDevelopmentOrPublisher,
                    styleOverrides: props.styleOverrides,
                    templateDownloadFilename: `${props.importIdentifier}-template`.toLowerCase(),
                    ...(uploadStep ? {uploadStep} : {}),
                }}
                // @ts-expect-error
                fields={props.fields}
                onResults={handleResults}
                onCancel={() => setIsUploaderModalOpen(false)}
                columnHooks={props.columnHooks}
                bulkRowHooks={props.bulkRowHooks}
            />
        </StackLegacy>
    );
}
